import type { AIBackgroundPromptPayload, StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import type { LayerData } from './components/Layer.types';
import type { LocationState } from 'history';

export type PrintDesignNavigationStep = 'cardFront' | 'cardBack' | 'envelope';
export type DigitalDesignNavigationStep = 'cardFront' | 'cardBack' | 'envelope';
export type DesignNavigationStep = PrintDesignNavigationStep | DigitalDesignNavigationStep;

export type PrintNavigationStep = PrintDesignNavigationStep | 'recipients' | 'review' | 'postCheckout';
export type DigitalNavigationStep = DigitalDesignNavigationStep | 'review' | 'postCheckout' | 'success';

export type NavigationStep = PrintNavigationStep | DigitalNavigationStep;

export interface NavigationContext<T extends string | number | symbol> {
  currentStep: T;
  steps: Record<
    T,
    {
      /**
       * A simple boolean that reflects whether a user has gone through this step.
       * The step component itself should employ business logic to determine if the step was actually completed.
       */
      hasCompleted: boolean;
    }
  >;

  navigateForward: () => void;

  goToStep: (step: T, state?: LocationState) => void;

  draftId: string;
}

/**
 * Not finalized data structure
 */
export type DraftData = {
  config: ThemeJson;
  template: {
    name?: string | null | undefined;
    themeId: string;
  };
  stationeryTemplateCategory: StationeryTemplateCategoryEnum;
};

export type Envelope = {
  layers: Array<LayerData>;
  layout: CardEnvelopeReturnAddressLayout;
  recipientAddressLayout?: CardEnvelopeRecipientAddressLayout;
  recipientAddressSelection?: CardEnvelopeRecipientAddressSelection;
  recipientAddressLayers?: Array<LayerData>;
  recipientIdList?: Array<string>;
  recipientIdWithAddressListLength?: number;
  design: CardEnvelopeDesign;
  showCountryInReturnAddress?: boolean;
};

export interface PageDimensions {
  readonly width: number;
  readonly height: number;
  readonly cutMargin: number;
  readonly safeZoneMargin: number;
}

export interface PageJson {
  dimensions?: PageDimensions;
  fill: string;
  layers: Array<LayerData>;
}

export interface ThemeJson {
  name: string;
  $schema: string;
  customizations: Pick<CardPriceModifiers, 'size' | 'paperType' | 'shape' | 'quantity' | 'delivery' | 'promoCode'> & {
    returnAddress?: CardAddress;
    deliveryAddress?: CardAddress;
    billingAddress?: CardAddress;
  };
  digitalCustomizations?: DigitalCardPriceModifiers & {
    backgroundColor?: string;
    envelopeColor?: string;
    envelopeAnimation?: string;
    interactive?: boolean; // Default true
  };
  filledAt?: string;
  productMetadata?: {
    selectedAttributes: {
      attributeId: string;
      optionId: string;
    }[];
  };
  card: {
    /**
     * Do not read this property directly. Instead use `getDimensions()` (see Card.utils.ts)
     */
    layout: PageDimensions;
    front: PageJson;
    back?: PageJson & { layout: string };
    envelope: Envelope;
  };
  imageHistory: Array<ImageHistoryElement>;
}

export interface ImageHistoryElement {
  type: string;
  url: string;
}

export interface ImageHistoryUpload extends ImageHistoryElement {
  type: 'upload';
}

export interface ImageHistoryVariation extends ImageHistoryElement {
  type: 'ai_prompt';
  promptPayload: AIBackgroundPromptPayload;
  baseUrl: string | null;
  resourceID: string | null;
}

export enum CardSize {
  classic = 'classic',
}

export enum CardPaperType {
  signature = 'standard',
  smooth = 'smooth',
  semiGloss = 'semiGloss',
  pearlescent = 'pearlescent',
  doubleThick = 'doubleThick',
  tripleThick = 'tripleThick',
}

export enum CardDieCut {
  rounded = 'rounded',
  rectangle = 'rectangle',
  arch = 'arch',
}

export enum CardDelivery {
  economy = 'economy',
  standard = 'standard',
  express = 'express',
  upsGroundCanadaStandard = 'ups_ground_canada_standard',
  upsCanadaExpressSaver = 'ups_canada_express_saver',
  upsInternationalExpressSaver = 'ups_international_express_saver',
}

export enum CardEnvelopeReturnAddressLayout {
  front = 'front',
  back = 'back',
  none = 'none',
}

export enum CardEnvelopeRecipientAddressLayout {
  front = 'front',
  none = 'none',
}

export enum CardEnvelopeRecipientAddressSelection {
  later = 'later',
  csv = 'csv',
  now = 'now',
}

export enum CardEnvelopeDesign {
  traditional = 'traditional',
  script = 'script',
  modern = 'modern',
}

export interface PriceConfig {
  quantity: {
    [Key in StationeryTemplateCategoryEnum]: Record<
      number,
      {
        quantity: number;
        totalPriceInMinorUnits: number;
        individualPriceInMinorUnits: number;
        individualPriceString: string;
        totalPriceString: string;
      }
    >;
  };
  shape: Record<
    CardDieCut,
    {
      code: string | undefined;
      label: string;
      individualPriceString: string | undefined;
      getTotalPriceStringByQuantity: (quantity: number) => string | undefined;
      getTotalPriceByQuantityInMinorUnits: (quantity: number) => number;
    }
  >;
  paperType: {
    [Key in CardPaperType]: Record<
      number,
      {
        quantity: number;
        totalPriceInMinorUnits: number;
        individualPriceString: string;
        totalPriceString: string;
      }
    >;
  };
  delivery: Record<CardDelivery, number>;
  returnAddress: Record<
    CardEnvelopeReturnAddressLayout,
    {
      individualPriceString: string | undefined;
      getTotalPriceStringByQuantity: (quantity: number) => string;
      getTotalPriceByQuantityInMinorUnits: (quantity: number) => number;
    }
  >;
  recipientAddress?: Record<
    CardEnvelopeRecipientAddressLayout,
    {
      individualPriceString: string | undefined;
      getTotalPriceStringByQuantity: (quantity: number) => string;
      getTotalPriceByQuantityInMinorUnits: (quantity: number) => number;
    }
  >;
}

export interface CardPriceModifiers {
  quantity: number;
  size: CardSize;
  paperType: CardPaperType;
  shape: CardDieCut;
  backLayout: string;
  returnAddress: CardEnvelopeReturnAddressLayout;
  recipientAddress?: CardEnvelopeRecipientAddressLayout;
  delivery?: CardDelivery;
  // Only storing the promo code value and not the config
  promoCode?: string;
  foilData?: { colorId: string; assetId: string };
}

export type DeliveryPackageKey = 'basic' | 'premium' | 'diamond';

export const DeliveryFeatures = {
  email: (quantity?: number) => `email${quantity ? `_${quantity}` : ''}`,
  sms: () => `sms`,
  international_sms: (quantity?: number) => `internationalsms${quantity ? `_${quantity}` : ''}`,
  link: () => `link`,
};

export interface DigitalCardPriceModifiers {
  // deliveryFeatures?: DigitalDeliveryFeatures[];
  quantity?: string;
  deliveryPackageKey?: DeliveryPackageKey;
}

export interface CardAddress {
  name: string;
  address1: string;
  address2: string;
  postalCode: string;
  city: string;
  state: string;
  country: string;
  countryCode: string;
  phoneNumber?: string;
}
